import { Button } from '../../components/Button';
import { DropdownTrigger } from '../../components/Dropdown';
import { Plus } from '../../icons';
import { Pressable } from '../../primitives/Pressable';
import { View } from '../../primitives/View';
import { DARK_LAYER1 } from '../../styles/elevation';
import { cn } from '../../utils/cn';

/**
 * "Add a source", as a TILE — Figma `55:25915` (Multi-Source-Set `44:596`).
 *
 * The add affordance moves between two places with the view: it is this tile
 * while the grid is showing, and the "+" beside the tabs once a material is
 * open. Never both. It is the LAST cell of the grid, after every material,
 * and it has no name or caption line under it (the frame draws none) — the
 * frame's own emptiness is the label.
 *
 * IT IS THE MENU'S TRIGGER, drawn as a tile: render it inside a `<Dropdown>`
 * (`DeckMaterialsAddMenu` does, through its `trigger` slot). Two things have
 * to hold at once, and they pull in opposite directions:
 *
 *  - THE WHOLE TILE IS THE TARGET, not just the button in the middle. A
 *    220×310 card with a 44px hit area at its centre reads as clickable
 *    everywhere and is clickable almost nowhere.
 *  - THE MENU ANCHORS ON THE "+", not on the card. A popover positions itself
 *    against whatever the DS `DropdownTrigger` wraps. Wrap the card and the
 *    menu lands at the card's far edge, a card-height away from the button
 *    that opened it — and the anchor wrapper hugs its content, so the card
 *    shrinks to the button's width with it. Both happened.
 *
 * So the anchor wraps the "+" ALONE, and the tile-sized presser is a sibling
 * of the "+" INSIDE that anchor, laid out of flow (`absolute inset-0`). The
 * anchor is a plain unpositioned span, so the presser escapes it and fills
 * the frame — the nearest positioned box — while the anchor's own box stays
 * the button's. Being inside the anchor is also what makes the presser TOGGLE
 * the menu rather than flicker it: the dropdown's outside-dismiss guard
 * exempts whatever the anchor contains.
 *
 * The presser is hidden from assistive tech and out of the tab order — it
 * adds a mouse target and nothing else, while the DS Button stays the one
 * labelled, focusable control — and it is a SIBLING because a `<button>`
 * inside a `<button>` is invalid HTML: the browser re-parents it (the same
 * constraint that puts `MaterialTile`'s ⋯ outside its frame).
 *
 * The PHONE opens a bottom sheet from this tile, not a popover, so there the
 * tile is no trigger at all: with `onPress` given it renders no
 * `DropdownTrigger`, needs no `<Dropdown>` around it, and the presser and the
 * "+" are plain siblings inside the frame — which is the positioned box the
 * presser fills on both platforms. (Inside a native `DropdownTrigger` the
 * wrapper is itself positioned, so the presser would cover only the "+";
 * that is why the sheet path does not go through the trigger.) The iPad
 * keeps its "+" in the bar (open item).
 */
export function AddMaterialTile({
  accessibilityLabel,
  frameHeight,
  onPress,
  className,
  testID,
}: {
  /** Accessible name of the "+" ("Add to this deck"). Pre-translated. */
  accessibilityLabel: string;
  /**
   * Open the host's own menu (the phone's bottom sheet) instead of the
   * surrounding `Dropdown`. With this set the tile must NOT be inside a
   * `<Dropdown>`; without it, it must.
   */
  onPress?: () => void;
  /**
   * Height of the portrait frame in px. Native MUST pass it (there is no CSS
   * aspect-ratio there); web may omit it and keep the 171:241 ratio from its
   * width. Same contract as `MaterialTile`.
   */
  frameHeight?: number;
  className?: string;
  testID?: string;
}) {
  const frameSize = frameHeight != null ? { height: frameHeight } : undefined;

  const body = (press: () => void) => (
    <>
      {/* The tile-sized target. Out of the tab order and out of the
          accessibility tree — the "+" beside it is the control; this
          is only the rest of the card agreeing to be pressed. */}
      <Pressable
        onPress={press}
        className="absolute inset-0"
        webProps={{ tabIndex: -1, 'aria-hidden': true }}
        nativeProps={{ accessible: false }}
        testID={testID ? `${testID}-surface` : undefined}
      />
      {/* Figma `55:25945`: the 44px secondary icon button, centred.
          Its 22px glyph is the frame's, one step up the DS's own `l`
          icon. The DS Button is itself positioned (`relative`, for
          its highlight overlay), which is what keeps it painted ABOVE
          the presser — a positioned box paints over an unpositioned
          one whatever the order, and the presser is positioned. */}
      <Button
        variant="secondary"
        size="l"
        icon={Plus}
        iconSize={22}
        accessibilityLabel={accessibilityLabel}
        onPress={press}
        testID={testID ? `${testID}-trigger` : undefined}
      />
    </>
  );

  return (
    <View className={cn('w-full flex-col items-center', className)} testID={testID}>
      <View
        className={cn(
          // The material frame's own geometry (radius 26, secondary-200
          // hairline), at the frame's lighter fill: secondary-100 at 30%,
          // a step BELOW the coverless material tile, because this one is
          // deliberately empty rather than missing a picture.
          //
          // `relative` is load-bearing: it is the box the tile-sized presser
          // fills. The View reset deliberately sets no position of its own.
          'relative w-full items-center justify-center overflow-hidden rounded-[26px] border border-secondary-200 bg-secondary-100/30',
          // Dark: no half step on the pane — the hairline is the edge, and the
          // pointer lifts it one clear step (elevation.ts).
          DARK_LAYER1,
          'dark:web:hover:bg-secondary-100',
          frameHeight == null && 'web:aspect-[171/241]',
        )}
        webProps={frameSize ? { style: frameSize } : undefined}
        nativeProps={frameSize ? { style: frameSize } : undefined}
        testID={testID ? `${testID}-frame` : undefined}>
        {onPress ? (
          body(onPress)
        ) : (
          <DropdownTrigger>{({ onPress: openMenu }) => body(openMenu)}</DropdownTrigger>
        )}
      </View>
    </View>
  );
}
(AddMaterialTile as { displayName?: string }).displayName = 'AddMaterialTile';

/**
 * The same "+" as a ROW CARD — the list view's last cell, where the grid has
 * its tile (Figma `107:1522`). It is the tile at a row's size and nothing
 * else: the same fill and hairline, the same centred "+", no name beside it,
 * and the same trigger contract (a `DropdownTrigger` on the "+" for web's
 * popover, `onPress` for the phone's sheet, and a card-sized presser laid out
 * of flow so the whole card is the target).
 */
export function AddMaterialRow({
  accessibilityLabel,
  onPress,
  className,
  testID,
}: {
  /** Accessible name of the "+" ("Add to this deck"). Pre-translated. */
  accessibilityLabel: string;
  /** See `AddMaterialTile`: set for the phone's sheet; unset inside a `<Dropdown>`. */
  onPress?: () => void;
  className?: string;
  testID?: string;
}) {
  const body = (press: () => void) => (
    <>
      <Pressable
        onPress={press}
        className="absolute inset-0"
        webProps={{ tabIndex: -1, 'aria-hidden': true }}
        nativeProps={{ accessible: false }}
        testID={testID ? `${testID}-surface` : undefined}
      />
      <Button
        variant="secondary"
        size="l"
        icon={Plus}
        iconSize={22}
        accessibilityLabel={accessibilityLabel}
        onPress={press}
        testID={testID ? `${testID}-trigger` : undefined}
      />
    </>
  );

  return (
    <View
      className={cn(
        // `relative` is load-bearing: the box the card-sized presser fills.
        'relative h-[100px] w-full items-center justify-center overflow-hidden rounded-[26px] border border-secondary-200 bg-secondary-100/30',
        // Dark: no half step on the pane — the hairline is the edge, and the
        // pointer lifts it one clear step (elevation.ts).
        DARK_LAYER1,
        'dark:web:hover:bg-secondary-100',
        className,
      )}
      testID={testID ? `${testID}-frame` : undefined}>
      {onPress ? (
        body(onPress)
      ) : (
        <DropdownTrigger>{({ onPress: openMenu }) => body(openMenu)}</DropdownTrigger>
      )}
    </View>
  );
}
(AddMaterialRow as { displayName?: string }).displayName = 'AddMaterialRow';
